"use client"

import { useEffect, useMemo, useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import Image from "next/image"
import Link from "next/link"
import { ArrowRight, Info, Package } from "lucide-react"
import { amazonUrl } from "@/lib/affiliate"
import { HeadlineHero, RelatedTools, ResultEmailCaptureCard, ToolPageLoadingFallback, ToolPageShell, ToolWideRegion } from "@/components/tools/shared"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, contentAnalytics, extractAsin, runWhenPageVisible, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { cn } from "@/lib/utils"
import { AffiliateDisclosure } from "@/components/tools/shared/affiliate-disclosure"
import { CollapsibleInline } from "@/components/tools/shared/collapsible-section"
import { ToolCrossLink } from "@/components/tools/shared/tool-cross-link"
import {
  BAG_SIZE_PRESETS,
  calculateFertilizerRate,
  FERTILIZER_PRESETS,
} from "@/lib/fertilizer-rate-calculator"

const TARGET_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5]

function parsePct(value: string | null, fallback: number): number {
  if (value === null || value.trim() === "") return fallback
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : fallback
}

function parseTarget(value: string | null): number {
  const n = Number(value)
  return TARGET_STEPS.includes(n) ? n : 1.0
}

function parseArea(value: string | null): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 && n <= 1_000_000 ? Math.round(n) : 5000
}

function parseBag(value: string | null): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 && n <= 100 ? n : 50
}

const SELECTED_CHIP = "border-2 border-green-600 bg-green-50"
const DEFAULT_CHIP = "border border-gray-200 bg-white hover:border-green-300"

const PICKS = [
  {
    name: "The Andersons PGF Complete 16-4-8",
    url: amazonUrl("B07SYLCKRH", "tool"),
    image: "https://m.media-amazon.com/images/I/41xelE+7eJL._SL500_.jpg",
  },
  {
    name: "Scotts Elite Spreader",
    url: amazonUrl("B077JBGG4H", "tool"),
    image: "https://m.media-amazon.com/images/I/317Nz1K222L._SL500_.jpg",
  },
]

const PICKS_SURFACE = "tool-fertilizer-rate-picks"

// Impression + click on the same surface string so PostHog can join
// affiliate_product_shown / affiliate_product_clicked and compute CTR. Extracted
// into a row component only because the impression hook must run per card.
function PickRow({ pick, pickIndex }: { pick: (typeof PICKS)[number]; pickIndex: number }) {
  const impressionRef = useAffiliateImpression(PICKS_SURFACE, {
    article_slug: "tool:fertilizer-rate-calculator",
    product_name: pick.name,
    affiliate_link: pick.url,
    asin: extractAsin(pick.url) ?? undefined,
    source_context: "result-picks",
    pick_index: pickIndex,
    position: pickIndex + 1,
  })

  return (
    <div
      ref={impressionRef}
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3"
    >
      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-gray-100 bg-white">
        {pick.image ? (
          <Image
            src={pick.image}
            alt={pick.name}
            width={56}
            height={56}
            className="h-full w-full object-contain p-1"
          />
        ) : (
          <Package className="h-5 w-5 text-gray-400" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900">{pick.name}</span>
        <a
          href={pick.url}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          data-affiliate="true"
          data-affiliate-placement={PICKS_SURFACE}
          onClick={() =>
            contentAnalytics.affiliateProductClicked(PICKS_SURFACE, {
              productName: pick.name,
              affiliateLink: pick.url,
              article_slug: "tool:fertilizer-rate-calculator",
              source_context: "result-picks",
              pick_index: pickIndex,
              position: pickIndex + 1,
            })
          }
          className="inline-flex min-h-11 items-center gap-1 text-[13px] font-semibold text-green-700 hover:text-green-800"
        >
          Check price on Amazon
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </div>
    </div>
  )
}

function pctInputProps(onChange: (v: number) => void) {
  return {
    type: "number" as const,
    min: 0,
    max: 100,
    inputMode: "decimal" as const,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value)
      onChange(Number.isNaN(v) ? 0 : Math.min(Math.max(v, 0), 100))
    },
    className:
      "w-full min-h-12 rounded-xl border border-gray-200 bg-white px-3.5 text-base font-bold text-gray-900 outline-none transition-colors focus:border-green-600 focus:ring-4 focus:ring-green-200/60",
  }
}

function FertilizerRateCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("fertilizer-rate-calculator")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  const [n, setN] = useState(() => parsePct(searchParams.get("n"), 10))
  const [p, setP] = useState(() => parsePct(searchParams.get("p"), 10))
  const [k, setK] = useState(() => parsePct(searchParams.get("k"), 10))
  const [targetN, setTargetN] = useState(() => parseTarget(searchParams.get("target")))
  const [area, setArea] = useState(() => parseArea(searchParams.get("area")))
  const [bagLbs, setBagLbs] = useState(() => parseBag(searchParams.get("bag")))
  // Kept as the raw string so a half-typed price ("12.") doesn't fight the user.
  const [bagPriceInput, setBagPriceInput] = useState("")
  const trackedUsageRef = useRef(false)

  const bagPrice = Number.parseFloat(bagPriceInput)
  const result = useMemo(
    () =>
      calculateFertilizerRate({
        n, p, k, targetN, areaSqFt: area, bagLbs,
        bagPrice: Number.isFinite(bagPrice) && bagPrice > 0 ? bagPrice : undefined,
      }),
    [n, p, k, targetN, area, bagLbs, bagPrice],
  )

  // Live-update tool: analytics fire once per session on the first deliberate
  // input change. setState hasn't re-rendered yet when this runs, so callers
  // pass the values they just set and the payload is computed from those.
  const trackOnce = (next?: Partial<{ n: number; p: number; k: number; targetN: number; area: number; bagLbs: number }>) => {
    if (trackedUsageRef.current) return
    trackedUsageRef.current = true
    trackUsage("fertilizer-rate-calculator")
    const inputs = { n, p, k, targetN, area, bagLbs, ...next }
    const nextResult = calculateFertilizerRate({ ...inputs, areaSqFt: inputs.area })
    analytics.calculatorUsed("fertilizer-rate", {
      inputs: { n: inputs.n, p: inputs.p, k: inputs.k, target: inputs.targetN, area: inputs.area, restored: false },
      result: { per_1000: nextResult.per1000Label, bags: nextResult.bags },
      source: "live_update",
    })
  }

  // Share-link restore. shareUrl carries the full input set, so a recipient sees
  // a finished result they never calculated and trackOnce (selection-change only)
  // never fires — the same undercount the lawn-size share path had. `restored`
  // separates it from a fresh run, matching grass_id_result_viewed. Fires once,
  // only when a share param is actually present, and only once the tab is
  // visible: an unconditional mount fire is the 2026-08-01 double-count bug, and
  // a hidden mount is a phantom session.
  const restoreTrackedRef = useRef(false)
  useEffect(() => {
    const hasShareParams = ["n", "p", "k", "target", "area", "bag"].some((key) =>
      Boolean(searchParams.get(key)),
    )
    if (!hasShareParams || restoreTrackedRef.current) return
    return runWhenPageVisible(() => {
      if (restoreTrackedRef.current) return
      restoreTrackedRef.current = true
      analytics.calculatorUsed("fertilizer-rate", {
        inputs: { n, p, k, target: targetN, area, restored: true },
        result: { per_1000: result.per1000Label, bags: result.bags },
        source: "url_params",
      })
    })
    // Restore state is read once from the URL the page mounted with; later
    // input changes are the interaction path's job, not this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const updateN = (v: number) => { setN(v); trackOnce({ n: v }) }
  const updateP = (v: number) => { setP(v); trackOnce({ p: v }) }
  const updateK = (v: number) => { setK(v); trackOnce({ k: v }) }
  const updateTarget = (v: number) => { setTargetN(v); trackOnce({ targetN: v }) }
  const updateArea = (v: number) => { setArea(v); trackOnce({ area: v }) }

  const selectPreset = (preset: (typeof FERTILIZER_PRESETS)[number]) => {
    setN(preset.n)
    setP(preset.p)
    setK(preset.k)
    trackOnce({ n: preset.n, p: preset.p, k: preset.k })
  }

  const shareUrl = `/tools/fertilizer-rate-calculator?n=${n}&p=${p}&k=${k}&target=${targetN}&area=${area}&bag=${bagLbs}`

  return (
    <ToolPageShell
      breadcrumbLabel="Fertilizer Rate Calculator"
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Fertilizer Rate Calculator"
          usageCount={usageCount}
        />
      }
    >
      <HeadlineHero
        eyebrow="Works with any bag"
        eyebrowIcon={<Package className="h-3.5 w-3.5" aria-hidden="true" />}
        title="How much of your bag"
        accent="per 1,000 sq ft?"
        subtitle="Enter the N-P-K on your bag and your lawn size to get pounds per 1,000 sq ft, total pounds, and how many bags to buy."
      />

      <ToolWideRegion>
        <div className="grid items-start gap-6 lg:grid-cols-2">
          {/* Inputs */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-elevation-2 sm:p-6">
            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              1 · Bag analysis (N-P-K)
            </p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Common bag analyses">
              {FERTILIZER_PRESETS.map((preset) => {
                const selected = preset.n === n && preset.p === p && preset.k === k
                return (
                  <button
                    key={preset.label}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => selectPreset(preset)}
                    className={cn(
                      "inline-flex min-h-11 items-center rounded-full px-4 text-sm font-bold text-gray-900 transition-colors",
                      selected ? SELECTED_CHIP : DEFAULT_CHIP,
                    )}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
            <div className="mt-3.5 grid grid-cols-3 gap-2.5">
              <div>
                <label htmlFor="npk-n" className="mb-1.5 block text-xs font-bold text-gray-500">
                  N (nitrogen)
                </label>
                <input id="npk-n" value={n} {...pctInputProps(updateN)} />
              </div>
              <div>
                <label htmlFor="npk-p" className="mb-1.5 block text-xs font-bold text-gray-500">
                  P (phosphorus)
                </label>
                <input id="npk-p" value={p} {...pctInputProps(updateP)} />
              </div>
              <div>
                <label htmlFor="npk-k" className="mb-1.5 block text-xs font-bold text-gray-500">
                  K (potassium)
                </label>
                <input id="npk-k" value={k} {...pctInputProps(updateK)} />
              </div>
            </div>
            {n <= 0 && (
              <p role="alert" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 text-sm font-semibold leading-relaxed text-amber-900">
                This calculator needs a nitrogen value above 0%. For a 0-N product, use the application rate printed on its label.
              </p>
            )}

            <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              2 · Target nitrogen rate
            </p>
            <input
              type="range"
              min={0.5}
              max={1.5}
              step={0.25}
              value={targetN}
              onChange={(e) => updateTarget(parseFloat(e.target.value))}
              aria-label="Target pounds of nitrogen per 1,000 square feet"
              className="w-full accent-green-600"
            />
            <div className="mt-1 flex items-center justify-between">
              <span className="text-xs text-gray-500">0.5 light</span>
              <span className="text-sm font-extrabold text-green-700">
                {result.targetLabel} lb N / 1,000 sq ft
              </span>
              <span className="text-xs text-gray-500">1.5 heavy</span>
            </div>

            <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              3 · Lawn size
            </p>
            <div className="relative">
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={area}
                aria-label="Lawn size in square feet"
                onChange={(e) => {
                  const v = parseFloat(e.target.value)
                  updateArea(Number.isNaN(v) ? 0 : Math.max(0, Math.round(v)))
                }}
                className="w-full min-h-13 rounded-xl border border-gray-200 bg-white pl-4 pr-[74px] text-[17px] font-bold text-gray-900 outline-none transition-colors focus:border-green-600 focus:ring-4 focus:ring-green-200/60"
              />
              <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-[13px] font-semibold text-gray-500">
                sq ft
              </span>
            </div>
            <p className="mt-2.5 text-[13px] text-gray-500">
              Not sure?{" "}
              <Link
                href="/tools/lawn-size-calculator"
                className="font-semibold text-green-600 underline underline-offset-2 hover:text-green-700"
              >
                Measure with the lawn size calculator
              </Link>
            </p>

            <CollapsibleInline
              title="Advanced: bag size & cost"
              storageKey="fertilizer-rate-advanced"
              className="mt-5"
            >
              <p className="mb-2 text-xs font-bold text-gray-500">Bag weight</p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Bag weight">
                {BAG_SIZE_PRESETS.map((size) => (
                  <button
                    key={size}
                    type="button"
                    role="radio"
                    aria-checked={size === bagLbs}
                    onClick={() => {
                      setBagLbs(size)
                      trackOnce({ bagLbs: size })
                    }}
                    className={cn(
                      "inline-flex min-h-11 items-center rounded-full px-3.5 text-[13px] font-semibold text-gray-900 transition-colors",
                      size === bagLbs ? SELECTED_CHIP : DEFAULT_CHIP,
                    )}
                  >
                    {size} lb
                  </button>
                ))}
              </div>
              <label htmlFor="bag-price" className="mt-3.5 mb-1.5 block text-xs font-bold text-gray-500">
                Price per bag (optional)
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  $
                </span>
                <input
                  id="bag-price"
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  placeholder="e.g. 59.88"
                  value={bagPriceInput}
                  onChange={(e) => {
                    setBagPriceInput(e.target.value)
                    trackOnce()
                  }}
                  className="w-full min-h-11 rounded-xl border border-gray-200 bg-white pl-8 pr-3.5 text-sm font-bold text-gray-900 outline-none transition-colors focus:border-green-600 focus:ring-4 focus:ring-green-200/60"
                />
              </div>
              <p className="mt-2 text-xs leading-relaxed text-gray-500">
                Adding a price shows what each feeding costs and the price per pound of actual
                nitrogen, which is the fair way to compare bags with different analyses.
              </p>
            </CollapsibleInline>
          </div>

          {/* Result: the peak frame, drawn with the ResultSuccessCard grammar
              (hero number + share, one-sentence interpretation, tiles)
              because the picks and cost tiles live inside it. */}
          <div data-tool-result="" className="tool-peak-card overflow-hidden">
            <div className="p-5 sm:p-6" aria-live="polite">
              <div className="flex items-start justify-between gap-3">
                <div data-result-heading="" className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <span
                      key={result.per1000Label}
                      className="tool-value-swap text-5xl font-extrabold leading-none tracking-tight tabular-nums text-green-700 sm:text-6xl"
                    >
                      {result.per1000Label}
                    </span>
                    <span className="text-xl font-bold text-green-700/80">lbs</span>
                  </div>
                  <p className="mt-1.5 text-sm font-medium text-gray-500">
                    of {result.npkLabel} per 1,000 sq ft
                  </p>
                </div>
                <UnifiedShareButton
                  url={shareUrl}
                  title="My fertilizer application rate"
                  text={`${result.npkLabel}: ${result.per1000Label} lbs per 1,000 sq ft, ${result.totalLbsLabel} lbs total (${result.bags} bag${result.bags === 1 ? "" : "s"})`}
                  trackingId="fertilizer-rate-calculator"
                  size="sm"
                />
              </div>
              <p className="mt-4 text-pretty text-base leading-relaxed text-gray-700">
                Spread {result.totalLbsLabel} lbs across your {result.areaLabel} sq ft lawn, which is{" "}
                {result.bags} × {result.bagLbs} lb bag{result.bags === 1 ? "" : "s"} to buy.
              </p>

              <p className="mt-5 mb-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                What that delivers per 1,000 sq ft
              </p>
              <div className="grid grid-cols-3 gap-2.5">
                {[
                  { value: result.nDeliveredLabel, label: "Nitrogen" },
                  { value: result.pDeliveredLabel, label: "Phosphorus" },
                  { value: result.kDeliveredLabel, label: "Potassium" },
                ].map((cell) => (
                  <div
                    key={cell.label}
                    className="rounded-xl border border-green-200/70 bg-white/70 px-3 py-2.5"
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{cell.label}</div>
                    <div className="mt-1 text-lg font-bold tabular-nums text-green-700">{cell.value} lb</div>
                  </div>
                ))}
              </div>

              {result.costPerAppLabel && (
                <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                  <div className="rounded-xl border border-green-200/70 bg-white/70 px-3 py-2.5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">This feeding</div>
                    <div className="mt-1 text-lg font-bold tabular-nums text-green-700">{result.costPerAppLabel}</div>
                  </div>
                  <div className="rounded-xl border border-green-200/70 bg-white/70 px-3 py-2.5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Per lb of N</div>
                    <div className="mt-1 text-lg font-bold tabular-nums text-green-700">{result.costPerLbNLabel ?? "—"}</div>
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 px-4 py-3.5">
                <Info className="mt-0.5 h-[18px] w-[18px] shrink-0 text-green-600" aria-hidden="true" />
                <p className="text-[13.5px] leading-relaxed text-green-900">
                  Spreader tip: start at half the recommended setting and make two passes at right
                  angles for even coverage.
                </p>
              </div>

              <p className="mt-5 mb-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Top picks
              </p>
              <div className="flex flex-col gap-2">
                {PICKS.map((pick, i) => (
                  <PickRow key={pick.name} pick={pick} pickIndex={i} />
                ))}
              </div>
              <AffiliateDisclosure className="mt-2.5" />
            </div>
          </div>
        </div>
      </ToolWideRegion>

      <ResultEmailCaptureCard
        className="mt-8"
        source="tool_result_fertilizer_rate"
        toolDisplayName="fertilizer rate"
        subcopy="Keep this fertilizer rate handy and track your lawn over time."
        accountLead={{
          redirectTo: shareUrl,
          headline: "Save my fertilizer rate",
          subcopy: "Keep this fertilizer rate in a free account and track your lawn over time.",
          onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_fertilizer_rate"),
        }}
        successSubcopy="Your lawn tips are on the way. Your fertilizer rate stays right here to revisit anytime."
        onCaptured={() => closeNewsletterModal()}
      />

      <ToolCrossLink
        className="mt-8"
        href="/tools/fertilizer-calculator"
        lead="Need the timing too?"
        body={<>See when to feed with the full fertilizer calculator&apos;s 12-month calendar.</>}
        cta="See my feeding calendar"
      />

      <RelatedTools
        currentToolId="fertilizer-rate-calculator"
        prefillValues={{}}
        className="mt-10"
      />
    </ToolPageShell>
  )
}

export function FertilizerRateCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="fertilizer-rate-calculator" />}>
      <SearchParamsHydrationGate>
        <FertilizerRateCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
